import { ApiClient } from '@/lib/axios/client';
import {
  Chatbot,
  CreateChatbotExcelDto,
  CreateChatbotUIDto,
  UpdateChatbotExcelDto,
  UpdateChatbotUIDto,
  ChatbotFilters,
  UpdateSettingsDto,
  ChatbotMode,
  TaskFilters,
  TaskRep,
  UpdateStatusRequest,
  Task,
  TaskApiPayload,
  TaskProperties,
  ApprovalConfig,
  ApprovalConfigUI,
  ApprovalLevel,
  ApprovalLevelUI,
  ApprovalVariableSource,
  ApprovalVariableSourceUI,
  ApprovalTemplateHeader,
  ApprovalTemplateHeaderUI,
  ApprovalTemplateBuilder,
  ApprovalTemplateBuilderUI,
  ApprovalTemplateButton,
  ApprovalTemplateButtonUI,
  StartnewConfig,
  StartnewConfigUI,
  DataSourceConfig,
  DataSourceConfigUI,
  SkipConfig,
  SkipConfigUI,
  SkipConditionUI,
  TaskAction,
  TaskType,
  ToggleStatusRequest,
  ChatbotData,
  TaskGroup,
  DataPoint,
  LinksResponse,
  WorkflowLinks,
} from './types';
import type { PaginatedData } from '@/lib/axios/types';

const BASE_PATH = '/api/v1/chatbots';

function transformApprovalVariableSourceToApiPayload(source: ApprovalVariableSourceUI): ApprovalVariableSource {
  return {
    kind: source.kind,
    static: source.static,
    task_ref: source.taskRef,
    field: source.field,
    decision: source.decision,
  };
}

function transformApprovalVariableSourceFromApiPayload(source: ApprovalVariableSource): ApprovalVariableSourceUI {
  return {
    kind: source.kind,
    static: source.static,
    taskRef: source.task_ref,
    field: source.field,
    decision: source.decision,
  };
}

function transformApprovalTemplateHeaderToApiPayload(header: ApprovalTemplateHeaderUI): ApprovalTemplateHeader {
  return {
    type: header.type,
    source: transformApprovalVariableSourceToApiPayload(header.source),
    value_source: header.valueSource ? transformApprovalVariableSourceToApiPayload(header.valueSource) : undefined,
  };
}

function transformApprovalTemplateHeaderFromApiPayload(header: ApprovalTemplateHeader): ApprovalTemplateHeaderUI {
  return {
    type: header.type,
    source: transformApprovalVariableSourceFromApiPayload(header.source),
    valueSource: header.value_source ? transformApprovalVariableSourceFromApiPayload(header.value_source) : undefined,
  };
}

function transformApprovalTemplateButtonToApiPayload(button: ApprovalTemplateButtonUI): ApprovalTemplateButton {
  return {
    sub_type: button.subType,
    payload: button.payload.map(transformApprovalVariableSourceToApiPayload),
    unique_action: button.uniqueAction,
  };
}

function transformApprovalTemplateButtonFromApiPayload(button: ApprovalTemplateButton): ApprovalTemplateButtonUI {
  return {
    subType: button.sub_type,
    payload: button.payload?.map(transformApprovalVariableSourceFromApiPayload) ?? [],
    uniqueAction: button.unique_action,
  };
}

function transformApprovalTemplateBuilderToApiPayload(builder: ApprovalTemplateBuilderUI): ApprovalTemplateBuilder {
  return {
    header: builder.header ? transformApprovalTemplateHeaderToApiPayload(builder.header) : undefined,
    body: builder.body.map(transformApprovalVariableSourceToApiPayload),
    buttons: builder.buttons?.map(transformApprovalTemplateButtonToApiPayload),
  };
}

function transformApprovalTemplateBuilderFromApiPayload(builder: ApprovalTemplateBuilder): ApprovalTemplateBuilderUI {
  return {
    header: builder.header ? transformApprovalTemplateHeaderFromApiPayload(builder.header) : undefined,
    body: builder.body?.map(transformApprovalVariableSourceFromApiPayload) ?? [],
    buttons: builder.buttons?.map(transformApprovalTemplateButtonFromApiPayload),
  };
}

function transformApprovalLevelToApiPayload(level: ApprovalLevelUI): ApprovalLevel {
  return {
    level: level.level,
    name: level.name,
    contact_type: level.contactType,
    approval_contacts: level.approvalContacts,
    approval_template: level.approvalTemplate,
    reject_template: level.rejectTemplate,
    approval_resolver: level.approvalResolver,
    template_builder: level.templateBuilder ? transformApprovalTemplateBuilderToApiPayload(level.templateBuilder) : undefined,
    quorum: level.quorum,
    quorum_count: level.quorumCount,
    timeout_hours: level.timeoutHours,
    on_timeout: level.onTimeout,
    escalate_to_level: level.escalateToLevel,
    on_reject: level.onReject,
  };
}

function transformApprovalLevelFromApiPayload(level: ApprovalLevel): ApprovalLevelUI {
  return {
    level: level.level,
    name: level.name,
    contactType: level.contact_type ?? 'manual',
    approvalContacts: level.approval_contacts ?? [],
    approvalTemplate: level.approval_template,
    rejectTemplate: level.reject_template,
    approvalResolver: level.approval_resolver,
    templateBuilder: level.template_builder ? transformApprovalTemplateBuilderFromApiPayload(level.template_builder) : undefined,
    quorum: level.quorum,
    quorumCount: level.quorum_count,
    timeoutHours: level.timeout_hours,
    onTimeout: level.on_timeout,
    escalateToLevel: level.escalate_to_level,
    onReject: level.on_reject,
  };
}

// Transform frontend ApprovalConfigUI to backend ApprovalConfig
function transformApprovalConfigToApiPayload(config?: ApprovalConfigUI): ApprovalConfig | undefined {
  if (!config) return undefined;
  return {
    levels: config.levels?.map(transformApprovalLevelToApiPayload),
    completion_policy: config.completionPolicy,
    max_bounces: config.maxBounces,
    contact_type: config.contactType,
    approval_contacts: config.approvalContacts,
    approval_template: config.approvalTemplate,
    approval_resolver: config.approvalResolver,
    template_builder: config.templateBuilder ? transformApprovalTemplateBuilderToApiPayload(config.templateBuilder) : undefined,
    parser_enabled: config.parserEnabled,
    parser_type: config.parserType,
    value_source_task_id: config.valueSourceTaskId,
    maker_pending_text: config.makerPendingText,
    maker_approved_text: config.makerApprovedText,
    maker_rejected_text: config.makerRejectedText,
    approver_approved_text: config.approverApprovedText,
    approver_rejected_text: config.approverRejectedText,
  };
}

// Transform backend ApprovalConfig to frontend ApprovalConfigUI
function transformApprovalConfigFromApiPayload(config?: ApprovalConfig): ApprovalConfigUI | undefined {
  if (!config) return undefined;
  return {
    levels: config.levels?.map(transformApprovalLevelFromApiPayload),
    completionPolicy: config.completion_policy,
    maxBounces: config.max_bounces,
    contactType: config.contact_type,
    approvalContacts: config.approval_contacts,
    approvalTemplate: config.approval_template,
    approvalResolver: config.approval_resolver,
    templateBuilder: config.template_builder ? transformApprovalTemplateBuilderFromApiPayload(config.template_builder) : undefined,
    parserEnabled: config.parser_enabled,
    parserType: config.parser_type,
    valueSourceTaskId: config.value_source_task_id,
    makerPendingText: config.maker_pending_text,
    makerApprovedText: config.maker_approved_text,
    makerRejectedText: config.maker_rejected_text,
    approverApprovedText: config.approver_approved_text,
    approverRejectedText: config.approver_rejected_text,
  };
}

// Transform frontend StartnewConfigUI to backend StartnewConfig
function transformStartnewConfigToApiPayload(config?: StartnewConfigUI): StartnewConfig | undefined {
  if (!config) return undefined;
  return {
    target_chatbot: config.targetChatbot,
    target_mapping_source: config.targetMappingSource,
    target_task: config.targetTask,
    default_target_key: config.defaultTargetKey,
    attribute_mapping: config.attributeMapping,
  };
}

// Transform backend StartnewConfig to frontend StartnewConfigUI
function transformStartnewConfigFromApiPayload(config?: StartnewConfig): StartnewConfigUI | undefined {
  if (!config) return undefined;
  return {
    targetChatbot: config.target_chatbot,
    targetMappingSource: config.target_mapping_source,
    targetTask: config.target_task,
    defaultTargetKey: config.default_target_key,
    attributeMapping: config.attribute_mapping,
  };
}

// Transform frontend DataSourceConfigUI to backend DataSourceConfig
function transformDataSourceConfigToApiPayload(config?: DataSourceConfigUI): DataSourceConfig | undefined {
  if (!config) return undefined;
  return {
    source: config.source,
    location_task: config.locationTask,
    region_task: config.regionTask,
    radius_km: config.radiusKm,
    limit: config.limit,
    empty_message: config.emptyMessage,
    dataset: config.dataset,
    filter_task: config.filterTask,
    lookup_field: config.lookupField,
    lookup_fields: config.lookupFields,
    match_mode: config.matchMode,
    title_field: config.titleField,
    description_field: config.descriptionField,
    mode: config.mode,
    answer_field: config.answerField,
    field_filters: fieldFiltersToApiPayload(config.fieldFilters),
  };
}

// Drops incomplete rows; undefined when none are left.
function fieldFiltersToApiPayload(rows?: { field: string; value: string }[]): Record<string, string> | undefined {
  const complete = (rows ?? []).filter((row) => row.field && row.value);
  if (complete.length === 0) return undefined;
  return Object.fromEntries(complete.map((row) => [row.field, row.value]));
}

// Transform backend DataSourceConfig to frontend DataSourceConfigUI
function transformDataSourceConfigFromApiPayload(config?: DataSourceConfig): DataSourceConfigUI | undefined {
  if (!config) return undefined;
  return {
    source: config.source,
    locationTask: config.location_task,
    regionTask: config.region_task,
    radiusKm: config.radius_km,
    limit: config.limit,
    emptyMessage: config.empty_message,
    dataset: config.dataset,
    filterTask: config.filter_task,
    lookupField: config.lookup_field,
    lookupFields: config.lookup_fields,
    matchMode: config.match_mode,
    titleField: config.title_field,
    descriptionField: config.description_field,
    mode: config.mode,
    answerField: config.answer_field,
    fieldFilters: config.field_filters
      ? Object.entries(config.field_filters).map(([field, value]) => ({ field, value }))
      : undefined,
  };
}

// Transform frontend SkipConfigUI to backend SkipConfig.
// Only conditions[] is written — the deprecated top-level condition_* fields are read-only.
function transformSkipConfigToApiPayload(config?: SkipConfigUI): SkipConfig | undefined {
  if (!config) return undefined;
  return {
    conditions: (config.conditions ?? [])
      .filter((condition): condition is SkipConditionUI & { conditionTask: number } =>
        condition.conditionTask !== undefined)
      .map(condition => ({
        condition_task: condition.conditionTask,
        condition_operator: condition.conditionOperator,
        condition_value: condition.conditionValue,
        condition_value_task: condition.conditionValueTask,
      })),
    condition_logic: config.conditionLogic ?? 'and',
    skip_mode: config.skipMode,
  };
}

// Transform backend SkipConfig to frontend SkipConfigUI.
// A config saved before multi-condition support carries its single condition in the
// deprecated top-level fields; read it as a one-element list so the UI shows it.
function transformSkipConfigFromApiPayload(config?: SkipConfig): SkipConfigUI | undefined {
  if (!config) return undefined;

  const conditions: SkipConditionUI[] = config.conditions?.length
    ? config.conditions.map(condition => ({
        conditionTask: condition.condition_task,
        conditionOperator: condition.condition_operator,
        conditionValue: condition.condition_value,
        conditionValueTask: condition.condition_value_task,
      }))
    : config.condition_task !== undefined
      ? [{
          conditionTask: config.condition_task,
          conditionOperator: config.condition_operator,
          conditionValue: config.condition_value,
          conditionValueTask: config.condition_value_task,
        }]
      : [];

  return {
    conditions,
    conditionLogic: config.condition_logic ?? 'and',
    skipMode: config.skip_mode,
  };
}

// Transform backend TaskProperties to frontend Task
function transformTaskFromApiPayload(taskProps: TaskProperties): Task {
  return {
    id: taskProps.id,
    name: taskProps.name,
    description: taskProps.description,
    type: taskProps.task_type,
    data: taskProps.task_type_metadata,
    actions: taskProps.task_actions,
    approvalConfig: transformApprovalConfigFromApiPayload(taskProps.approval_config),
    validationConfig: taskProps.validation_config,
    extractionConfig: taskProps.extraction_config,
    dataSourceConfig: transformDataSourceConfigFromApiPayload(taskProps.data_source_config),
    startnewConfig: transformStartnewConfigFromApiPayload(taskProps.startnew_config),
    autoFillConfig: taskProps.auto_fill_config,
    skipConfig: transformSkipConfigFromApiPayload(taskProps.skip_config),
    counterConfig: taskProps.counter_config
      ? {
          identityTaskId: taskProps.counter_config.identity_task_id,
          countField: taskProps.counter_config.count_field,
          codeTaskId: taskProps.counter_config.code_task_id,
          sharedIdentities: taskProps.counter_config.shared_identities,
        }
      : undefined,
    writebackConfigs: taskProps.writeback_configs?.map((config) => ({
      dataset: config.dataset,
      keyRef: config.key_ref,
      matchField: config.match_field,
      mode: config.mode,
      labelRef: config.label_ref,
      parentKeyRef: config.parent_key_ref,
      fields: config.fields ?? {},
    })),
    subtasks: taskProps.sub_tasks?.map(transformTaskFromApiPayload),
    mutable: taskProps.mutable,
    maskOutput: taskProps.mask_output ?? false,
    requirePrefill: taskProps.require_prefill ?? false,
    softTtlValue: taskProps.soft_ttl_value,
    softTtlUnit: taskProps.soft_ttl_unit as 'hours' | 'days' | undefined,
    documentLinks: taskProps.document_links ?? [],
    tags: taskProps.tags ?? [],
  };
}

// Transform frontend Task to backend TaskApiPayload
function transformTaskToApiPayload(task: Task): TaskApiPayload {
  // Include approval_config if MAKERCHECKER (APPROVAL) or TEAM_ALERT (NOTIFY) is
  // selected — both reuse the same contacts + template config.
  const hasMakerChecker = task.actions?.includes(TaskAction.MAKERCHECKER);
  const hasTeamAlert = task.actions?.includes(TaskAction.TEAM_ALERT);

  // Only include startnew_config if STARTNEW (START) action is selected
  const hasStartnew = task.actions?.includes(TaskAction.STARTNEW);

  // auto_fill_config silently pre-completes later tasks with a field from the matched
  // master-data record, or — for a verification-enabled photo/document/media task —
  // from that task's own mirrored extraction result (extractionGate.mirrorFields,
  // internal/workflow/types/extraction.go). dynamic_choice always supports it;
  // text_required and location_check only once a master_data lookup is configured
  // (same condition as shouldIncludeDataSource below); photo/document/media only
  // once extraction verification is switched on (same condition TaskConfigPanel.tsx
  // uses to decide whether to even show the Auto-Fill section for these types).
  const shouldIncludeAutoFill =
    task.autoFillConfig &&
    Object.keys(task.autoFillConfig).length > 0 &&
    (task.type === TaskType.DYNAMIC_CHOICE ||
      ((task.type === TaskType.TEXT || task.type === TaskType.LOCATION_CHECK) &&
        task.dataSourceConfig?.source === 'master_data' && !!task.dataSourceConfig?.dataset) ||
      ([TaskType.PHOTO, TaskType.DOCUMENT, TaskType.MEDIA].includes(task.type) &&
        !!task.extractionConfig?.enabled));

  // Only include skip_config if SKIPPABLE (SKIP) action is selected and at least one
  // condition names a task — a condition with no task selected is an unfinished row.
  const hasSkippable = task.actions?.includes(TaskAction.SKIPPABLE);
  const shouldIncludeSkipConfig = hasSkippable &&
    !!task.skipConfig?.conditions?.some(condition => condition.conditionTask !== undefined);

  // Only include validation_config if task type is NUMBER and validationConfig exists
  // Support both static threshold and dynamic reference_task validations
  const shouldIncludeValidation = task.type === TaskType.NUMBER &&
    task.validationConfig &&
    task.validationConfig.type &&
    (task.validationConfig.threshold !== undefined || task.validationConfig.reference_task !== undefined);

  // Include data_source_config for DYNAMIC_CHOICE (any source), TEXT (master_data
  // lookup), LOCATION_CHECK (master_data nearby-search verification), ROLE
  // (master_data "assisted" roster — optional, unset means always "self"), and
  // COUNTER (master_data daily-cap lookup — required)
  const shouldIncludeDataSource =
    (task.type === TaskType.DYNAMIC_CHOICE && task.dataSourceConfig?.source) ||
    (task.type === TaskType.TEXT && task.dataSourceConfig?.source === 'master_data' && task.dataSourceConfig?.dataset) ||
    (task.type === TaskType.LOCATION_CHECK && task.dataSourceConfig?.source === 'master_data' && task.dataSourceConfig?.dataset) ||
    (task.type === TaskType.ROLE && task.dataSourceConfig?.source === 'master_data' && task.dataSourceConfig?.dataset) ||
    (task.type === TaskType.COUNTER && task.dataSourceConfig?.source === 'master_data' && task.dataSourceConfig?.dataset);

  // Only include counter_config for COUNTER tasks
  const shouldIncludeCounterConfig = task.type === TaskType.COUNTER;

  // Only include writeback_configs if WRITEBACK is enabled, and only the usable
  // policies within it — dataset and keyRef are required per-policy for a merge to
  // have anywhere to land (create mode may leave keyRef blank to generate a key), but
  // one incomplete policy shouldn't drop the others.
  const hasWriteback = task.actions?.includes(TaskAction.WRITEBACK);
  const usableWritebackConfigs = task.writebackConfigs?.filter((config) => !!config.dataset && (!!config.keyRef || config.mode === 'create'));
  const shouldIncludeWriteback = hasWriteback && !!usableWritebackConfigs?.length;

  const basePayload: TaskApiPayload = {
    id: task.id,
    name: task.name,
    description: task.description,
    type: task.type,
    data: task.data,
    actions: task.actions?.length ? task.actions : undefined, 
    sub_tasks: task.subtasks?.map(transformTaskToApiPayload),
    mutable: task.mutable,
    mask_output: task.maskOutput ?? false,
    require_prefill: task.requirePrefill || undefined,
    soft_ttl_value: task.softTtlValue,
    soft_ttl_unit: task.softTtlUnit,
    document_links: task.documentLinks,
    tags: task.tags ?? [],
  };

  // Add validation_config only if needed
  if (shouldIncludeValidation) {
    basePayload.validation_config = task.validationConfig;
  }

  // Add extraction_config only when verification is actually switched on. An
  // enabled:false object would be indistinguishable from "configured then turned off"
  // in the stored JSONB, and the backend treats absent and disabled identically —
  // so omit it and keep the task list clean.
  if (task.extractionConfig?.enabled) {
    basePayload.extraction_config = task.extractionConfig;
  }

  // Add data_source_config only if needed
  if (shouldIncludeDataSource) {
    basePayload.data_source_config = transformDataSourceConfigToApiPayload(task.dataSourceConfig);
  }

  // Add approval_config if MAKERCHECKER or TEAM_ALERT is enabled
  if (hasMakerChecker || hasTeamAlert) {
    basePayload.approval_config = transformApprovalConfigToApiPayload(task.approvalConfig);
  }

  // Add startnew_config if STARTNEW is enabled
  if (hasStartnew && task.startnewConfig) {
    basePayload.startnew_config = transformStartnewConfigToApiPayload(task.startnewConfig);
  }

  // Add auto_fill_config for dynamic_choice tasks with configured entries
  if (shouldIncludeAutoFill) {
    basePayload.auto_fill_config = task.autoFillConfig;
  }

  // Add skip_config if SKIPPABLE is enabled with conditional logic
  if (shouldIncludeSkipConfig) {
    basePayload.skip_config = transformSkipConfigToApiPayload(task.skipConfig);
  }

  // Add counter_config for COUNTER tasks (every field within it is optional)
  if (shouldIncludeCounterConfig) {
    basePayload.counter_config = {
      identity_task_id: task.counterConfig?.identityTaskId,
      count_field: task.counterConfig?.countField,
      code_task_id: task.counterConfig?.codeTaskId,
      shared_identities: task.counterConfig?.sharedIdentities,
    };
  }

  // Add writeback_configs if WRITEBACK is enabled with at least one usable policy
  if (shouldIncludeWriteback && usableWritebackConfigs) {
    basePayload.writeback_configs = usableWritebackConfigs.map((config) => ({
      dataset: config.dataset,
      key_ref: config.keyRef,
      match_field: config.mode === 'create' ? undefined : config.matchField,
      mode: config.mode,
      label_ref: config.mode ? config.labelRef || undefined : undefined,
      parent_key_ref: config.mode ? config.parentKeyRef || undefined : undefined,
      fields: config.fields ?? {},
    }));
  }

  return basePayload;
}


// Transform frontend CreateChatbotUIDto to API payload
function transformCreateChatbotUIDto(data: Omit<CreateChatbotUIDto, 'tasks'> & { tasks: Task[] }): CreateChatbotUIDto {
  return {
    ...data,
    tasks: data.tasks.map(transformTaskToApiPayload),
  };
}

// Transform frontend UpdateChatbotUIDto to API payload
function transformUpdateChatbotUIDto(data: Omit<UpdateChatbotUIDto, 'tasks'> & { tasks: Task[] }): UpdateChatbotUIDto {
  return {
    ...data,
    tasks: data.tasks.map(transformTaskToApiPayload),
  };
}

// Export transformation functions for use in other modules
export { transformTaskFromApiPayload };

export class ChatbotApi {
  static async getAll(filters?: ChatbotFilters): Promise<PaginatedData<Chatbot>> {
    return await ApiClient.get<PaginatedData<Chatbot>>(BASE_PATH, {
      params: filters,
    });
  }

  static async getById(id: string): Promise<Chatbot> {
    return await ApiClient.get<Chatbot>(`${BASE_PATH}/${id}`);
  }

  static async create(mode: ChatbotMode, data: CreateChatbotExcelDto | (Omit<CreateChatbotUIDto, 'tasks'> & { tasks: Task[] })): Promise<Chatbot> {
    const payload = mode === 'ui' && 'tasks' in data ? transformCreateChatbotUIDto(data as Omit<CreateChatbotUIDto, 'tasks'> & { tasks: Task[] }) : data;
    return await ApiClient.post<Chatbot>(`${BASE_PATH}`, payload, {
      params: { mode },
    });
  }

  static async update(id: string, mode: ChatbotMode, data: UpdateChatbotExcelDto | (Omit<UpdateChatbotUIDto, 'tasks'> & { tasks: Task[] })): Promise<Chatbot> {
    const payload = mode === 'ui' && 'tasks' in data ? transformUpdateChatbotUIDto(data as Omit<UpdateChatbotUIDto, 'tasks'> & { tasks: Task[] }) : data;
    return await ApiClient.patch<Chatbot>(`${BASE_PATH}/${id}`, payload, {
      params: { mode },
    });
  }

  static async delete(id: string): Promise<void> {
    await ApiClient.delete<Chatbot>(`${BASE_PATH}/${id}`);
  }

  static async block(id: string, data: ToggleStatusRequest): Promise<void> {
    await ApiClient.patch<Chatbot>(`${BASE_PATH}/${id}/block`, data);
  }

  /** Sets the org's workflow display order — position N in `order` becomes sort_order N. */
  static async reorder(order: string[]): Promise<void> {
    await ApiClient.patch<void>(`${BASE_PATH}/order`, { order });
  }

  // An .xlsx of the workflows' titles, codes, WhatsApp links and S3 QR code URLs, in id order.
  static async exportQrCodes(chatbotIds: string[]): Promise<Blob> {
    return await ApiClient.post<Blob>(
      `${BASE_PATH}/qr-export`,
      { chatbot_ids: chatbotIds },
      { responseType: 'blob' }
    );
  }

  static async deleteUser(id: string, userId: string): Promise<void> {
    await ApiClient.delete<Chatbot>(`${BASE_PATH}/${id}/team/${userId}`);
  }

  static async updateSettings(id: string, data: UpdateSettingsDto): Promise<Chatbot> {
    return await ApiClient.post<Chatbot>(`${BASE_PATH}/${id}/settings`, data);
  }

  /**
   * Generates Flow JSON from the chatbot's current task list and returns it as-is —
   * a dry run that never calls Meta or writes to the database. Use to inspect/verify
   * the generated JSON before syncFlow, which does hit the Graph API.
   */
  static async previewFlow(id: string): Promise<{ flow_json: Record<string, unknown> }> {
    return await ApiClient.get<{ flow_json: Record<string, unknown> }>(`${BASE_PATH}/${id}/flow/preview`);
  }

  /**
   * Generates Flow JSON from the chatbot's current task list and creates the Flow on
   * Meta (if none exists yet) or re-uploads the JSON (if still DRAFT and the task list
   * changed). Never publishes — see publishFlow.
   */
  static async syncFlow(id: string): Promise<Chatbot> {
    return await ApiClient.post<Chatbot>(`${BASE_PATH}/${id}/flow/sync`);
  }

  /**
   * Publishes the chatbot's currently-synced Flow JSON, making it live. Effectively
   * irreversible for that JSON version — call syncFlow first.
   */
  static async publishFlow(id: string): Promise<Chatbot> {
    return await ApiClient.post<Chatbot>(`${BASE_PATH}/${id}/flow/publish`);
  }

  /**
   * Deletes the chatbot's Flow object on Meta and clears its synced/published Flow
   * state in the backend. Meta does not allow deleting a PUBLISHED Flow.
   */
  static async deleteFlow(id: string): Promise<Chatbot> {
    return await ApiClient.delete<Chatbot>(`${BASE_PATH}/${id}/flow`);
  }

  static async fetchTasks(id: string, filters?: TaskFilters): Promise<TaskRep[]> {
    return await ApiClient.get<TaskRep[]>(`${BASE_PATH}/${id}/tasks`,{
      params: filters,
    });
  }

  static async fetchDataGroups(id: string, filters?: TaskFilters): Promise<ChatbotData> {
    return await ApiClient.get<ChatbotData>(`${BASE_PATH}/${id}/data`,{
      params: filters,
    });
  }

  static async fetchDataGroup(id: string, source_key: string): Promise<TaskGroup> {
    return await ApiClient.get<TaskGroup>(`${BASE_PATH}/${id}/data/${source_key}`);
  }

  /**
   * @deprecated Use editTaskData instead for audit trail support
   */
  static async updateDataGroup(id: string,request: DataPoint[]): Promise<TaskGroup> {
    return await ApiClient.patch<TaskGroup>(`${BASE_PATH}/${id}/data/${id}`, request);
  }

  /**
   * Edit task data with audit trail
   * Uses the workflows/command API with dataedit_ command pattern
   * @param userId - User ID making the edit
   * @param orgId - Organization ID
   * @param botCode - Chatbot short code (6 characters)
   * @param taskId - Task XID
   * @param repId - Task rep ID (database row ID)
   * @param noteId - Note ID being edited
   * @param newValue - New value for the note
   */
  static async editTaskData(
    userId: string,
    orgId: string,
    botCode: string,
    taskId: string,
    repId: number,
    noteId: string,
    newValue: string
  ): Promise<void> {
    const command = `dataedit_${botCode}_taskId_${taskId}_repId_${repId}_noteId_${noteId}`;
    await ApiClient.post('/api/v1/workflows/command', {
      user_id: userId,
      org_id: orgId,
      command: command,
      text: newValue,
      message_type: 'text'
    });
  }


  static async fetchTask(id: string, taskId: string): Promise<TaskRep> {
    return await ApiClient.get<TaskRep>(`${BASE_PATH}/${id}/tasks/${taskId}`);
  }

  static async updateTask(id: string, taskId: string, data: UpdateStatusRequest): Promise<TaskRep> {
    return await ApiClient.patch<TaskRep>(`${BASE_PATH}/${id}/tasks/${taskId}`, data);
  }

  // Workflow Links API Methods

  /**
   * Get workflow links for a chatbot
   */
  static async getLinks(code: string): Promise<LinksResponse> {
    return await ApiClient.get<LinksResponse>(`${BASE_PATH}/${code}/links`);
  }

  /**
   * Update workflow links for a chatbot
   * @param code - Chatbot short code
   * @param links - Map of link keys to target workflow codes
   */
  static async updateLinks(code: string, links: WorkflowLinks): Promise<LinksResponse> {
    return await ApiClient.patch<LinksResponse>(`${BASE_PATH}/${code}/links`, { links });
  }

  /**
   * Delete a specific workflow link
   * @param code - Chatbot short code
   * @param key - Link key to delete
   */
  static async deleteLink(code: string, key: string): Promise<LinksResponse> {
    return await ApiClient.delete<LinksResponse>(`${BASE_PATH}/${code}/links/${key}`);
  }
}